import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiTrash2, FiPlus, FiX, FiGrid } = FiIcons;

const ManageClusters = () => {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCluster, setCurrentCluster] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '' });

  useEffect(() => {
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    try {
      const response = await apiService.getClusters();
      if (response.status === 'success') {
        setClusters(response.data);
      }
    } catch (error) {
      console.error('Error fetching clusters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentCluster) {
        await apiService.updateCluster(currentCluster.id, formData);
      } else {
        await apiService.createCluster(formData);
      }
      fetchClusters();
      closeModal();
    } catch (error) {
      console.error('Error saving cluster:', error);
      alert('Fehler beim Speichern des Clusters');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie dieses Cluster wirklich löschen?')) {
      try {
        await apiService.deleteCluster(id);
        fetchClusters();
      } catch (error) {
        console.error('Error deleting cluster:', error);
        alert('Fehler beim Löschen des Clusters');
      }
    }
  };

  const openModal = (cluster = null) => {
    if (cluster) {
      setCurrentCluster(cluster);
      setFormData({ title: cluster.title, description: cluster.description });
    } else {
      setCurrentCluster(null);
      setFormData({ title: '', description: '' });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCluster(null);
    setFormData({ title: '', description: '' });
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-brand-dark flex items-center">
          <SafeIcon icon={FiGrid} className="mr-2" />
          Cluster verwalten
        </h2>
        <button
          onClick={() => openModal()}
          className="flex items-center px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark transition-colors"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Neues Cluster
        </button>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {clusters.map((cluster) => (
            <li key={cluster.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
              <div>
                <h3 className="text-lg font-medium text-brand-dark">{cluster.title}</h3>
                <p className="text-sm text-gray-500">{cluster.description}</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => openModal(cluster)}
                  className="text-brand-secondary hover:text-brand-dark"
                >
                  <SafeIcon icon={FiEdit2} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(cluster.id)}
                  className="text-red-600 hover:text-red-900"
                >
                  <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                </button>
              </div>
            </li>
          ))}
          {clusters.length === 0 && (
            <li className="px-6 py-4 text-center text-gray-500">Keine Cluster vorhanden</li>
          )}
        </ul>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">
                {currentCluster ? 'Cluster bearbeiten' : 'Neues Cluster'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark"
                >
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageClusters;